// Graphique CA du Dashboard : COURBES (émis / encaissé), interactif et
// navigable dans l'historique par fenêtres de 6 mois (GET /b2b/ca-series).
// Conforme aux règles dataviz du repo : paire catégorielle validée (jetons
// --chart-emis / --chart-encaisse, clair et sombre), lignes 2px avec points,
// crosshair + tooltip au survol (cible plus large que la marque), grille
// discrète, légende obligatoire à 2 séries, table masquée pour les lecteurs
// d'écran. Un seul axe, zéro dépendance.
import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { apiGet } from '../lib/api';
import type { MonthlyCaPoint } from '../data/live';
import { Button, eur } from './ui';

const MONTH_LABELS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

function labelOf(month: string): string {
  const m = Number(month.slice(5, 7));
  const y = month.slice(2, 4);
  return `${MONTH_LABELS[m - 1] ?? month} ${y}`;
}

/* Libellés d'axe compacts : unité collée au nombre par une espace insécable,
   comme le helper eur() (cf. components/ui.tsx). */
function kEur(v: number): string {
  return v >= 1000
    ? `${(v / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })}\u00A0k\u20AC`
    : `${Math.round(v)}\u00A0\u20AC`;
}

/** Plafond « rond » de l'axe (1-2-5 x 10^n) pour des graduations lisibles. */
function niceMax(v: number): number {
  if (v <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(v));
  for (const mult of [1, 2, 5, 10]) {
    if (v <= mag * mult) return mag * mult;
  }
  return mag * 10;
}

/** Mois courant ('YYYY-MM', UTC) et décalage de mois sur une clé 'YYYY-MM'. */
const currentMonth = (): string => new Date().toISOString().slice(0, 7);
function shiftMonth(month: string, offset: number): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y ?? 2026, (m ?? 1) - 1 + offset, 1)).toISOString().slice(0, 7);
}

const W = 560;
const H = 210;
const PAD_LEFT = 52;
const PAD_RIGHT = 14;
const PAD_TOP = 12;
const PAD_BOTTOM = 26;

export function MonthlyCaChart({ monthly }: { monthly: MonthlyCaPoint[] }): ReactElement {
  // Navigation : `end` = dernier mois de la fenêtre affichée. La fenêtre
  // initiale (mois courant) vient du payload dashboard, les autres sont
  // chargées à la demande et mises en cache par clé de fin.
  const [end, setEnd] = useState(currentMonth());
  const [cache, setCache] = useState<Record<string, MonthlyCaPoint[]>>({});
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState<number | null>(null);

  const atNow = end === currentMonth();
  const data = atNow ? monthly : cache[end] ?? [];

  useEffect(() => {
    if (atNow || cache[end]) return;
    let alive = true;
    setLoading(true);
    apiGet<{ monthly: MonthlyCaPoint[] }>(`/api/b2b/ca-series?months=6&end=${end}`)
      .then((r) => { if (alive) setCache((c) => ({ ...c, [end]: r.monthly })); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [end, atNow, cache]);

  const geom = useMemo(() => {
    const plotW = W - PAD_LEFT - PAD_RIGHT;
    const plotH = H - PAD_TOP - PAD_BOTTOM;
    const baseline = PAD_TOP + plotH;
    const top = niceMax(Math.max(1, ...data.map((p) => Math.max(p.emis, p.encaisse, 0))));
    const slot = data.length > 0 ? plotW / data.length : plotW;
    const xOf = (i: number): number => PAD_LEFT + i * slot + slot / 2;
    const yOf = (v: number): number => baseline - (Math.max(v, 0) / top) * plotH;
    return { plotW, plotH, baseline, top, slot, xOf, yOf };
  }, [data]);

  const hasData = monthly.length > 0;
  if (!hasData) {
    return (
      <p className="muted" style={{ minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>
        Le graphique se remplira avec vos premières factures émises et encaissées.
      </p>
    );
  }

  const path = (key: 'emis' | 'encaisse'): string =>
    data.map((p, i) => `${i === 0 ? 'M' : 'L'}${geom.xOf(i).toFixed(1)},${geom.yOf(p[key]).toFixed(1)}`).join(' ');
  const hovered = hover !== null ? data[hover] : undefined;

  return (
    <div>
      <div style={{ position: 'relative', opacity: loading ? 0.5 : 1, transition: 'opacity .15s' }}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img"
          aria-label="Chiffre d'affaires émis et encaissé par mois" onMouseLeave={() => setHover(null)}>
          {[0, 0.5, 1].map((t) => {
            const y = geom.baseline - t * geom.plotH;
            return (
              <g key={t}>
                <line x1={PAD_LEFT} x2={W - PAD_RIGHT} y1={y} y2={y}
                  stroke="currentColor" opacity={t === 0 ? 0.35 : 0.12} strokeWidth={1} />
                <text x={PAD_LEFT - 6} y={y + 3.5} textAnchor="end" fontSize={10.5} fill="currentColor" opacity={0.55}>
                  {kEur(geom.top * t)}
                </text>
              </g>
            );
          })}
          {/* Crosshair du mois survolé, sous les courbes. */}
          {hover !== null && (
            <line x1={geom.xOf(hover)} x2={geom.xOf(hover)} y1={PAD_TOP} y2={geom.baseline}
              stroke="currentColor" opacity={0.25} strokeWidth={1} />
          )}
          <path d={path('emis')} fill="none" stroke="var(--chart-emis)" strokeWidth={2}
            strokeLinejoin="round" strokeLinecap="round" />
          <path d={path('encaisse')} fill="none" stroke="var(--chart-encaisse)" strokeWidth={2}
            strokeLinejoin="round" strokeLinecap="round" />
          {data.map((p, i) => (
            <g key={p.month}>
              <circle cx={geom.xOf(i)} cy={geom.yOf(p.emis)} r={hover === i ? 4.5 : 3}
                fill="var(--chart-emis)" stroke="var(--surface-card, #fff)" strokeWidth={1.5} />
              <circle cx={geom.xOf(i)} cy={geom.yOf(p.encaisse)} r={hover === i ? 4.5 : 3}
                fill="var(--chart-encaisse)" stroke="var(--surface-card, #fff)" strokeWidth={1.5} />
              {/* Cible de survol : toute la colonne du mois. */}
              <rect x={PAD_LEFT + i * geom.slot} y={PAD_TOP} width={geom.slot} height={geom.plotH}
                fill="transparent" onMouseEnter={() => setHover(i)} />
              <text x={geom.xOf(i)} y={H - 8} textAnchor="middle" fontSize={10.5} fill="currentColor"
                opacity={hover === i ? 0.95 : 0.65}>
                {labelOf(p.month)}
              </text>
            </g>
          ))}
        </svg>
        {/* Tooltip HTML : suit le mois survolé (texte en encre, pastilles de série). */}
        {hovered && hover !== null && (
          <div style={{
            position: 'absolute', top: 4,
            left: `${(geom.xOf(hover) / W) * 100}%`,
            transform: `translateX(${hover >= data.length - 2 ? '-100%' : '8px'})`,
            background: 'var(--surface-card, #fff)', border: '1px solid rgba(128,128,128,.25)',
            borderRadius: 8, padding: '8px 10px', fontSize: 12.5, pointerEvents: 'none',
            boxShadow: '0 4px 14px rgba(0,0,0,.10)', whiteSpace: 'nowrap',
          }}>
            <b style={{ display: 'block', marginBottom: 4 }}>{labelOf(hovered.month)}</b>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span aria-hidden style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--chart-emis)' }} />
              Émis : {eur(hovered.emis)}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span aria-hidden style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--chart-encaisse)' }} />
              Encaissé : {eur(hovered.encaisse)}
            </span>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
        <Button variant="ghost" size="sm" icon="chev-l" aria-label="6 mois précédents" disabled={loading}
          onClick={() => { setHover(null); setEnd((e) => shiftMonth(e, -6)); }} />
        <span style={{ display: 'flex', gap: 18, justifyContent: 'center', flex: 1, fontSize: 12.5 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span aria-hidden style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--chart-emis)' }} />
            Émis
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span aria-hidden style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--chart-encaisse)' }} />
            Encaissé
          </span>
        </span>
        <Button variant="ghost" size="sm" icon="chev-r" aria-label="6 mois suivants" disabled={atNow || loading}
          onClick={() => { setHover(null); setEnd((e) => { const n = shiftMonth(e, 6); return n > currentMonth() ? currentMonth() : n; }); }} />
      </div>
      {/* Table équivalente pour les lecteurs d'écran. */}
      <div style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' }}>
        <table>
          <caption>Chiffre d'affaires par mois (fenêtre affichée)</caption>
          <thead><tr><th>Mois</th><th>Émis</th><th>Encaissé</th></tr></thead>
          <tbody>
            {data.map((p) => (
              <tr key={p.month}><td>{labelOf(p.month)}</td><td>{eur(p.emis)}</td><td>{eur(p.encaisse)}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
